import { groupBy } from 'lodash';
import { type VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';
import { DEFAULT_FONT_FAMILY, type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { type FontItem } from '@meitu/xdesign-widgets';
import { FontFamilySelector } from '@/components/font-family-selector';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { getUsingFonts } from '@/utils/font';
import { type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';

type Props = WidgetCommonProps & {
    fonts: FontItem[];
    enableRemoteSearch?: boolean;
};

const FALLBACK_FONT_FAMILY = DEFAULT_FONT_FAMILY().name;

export const FontFamily: React.FC<Props> = ({
    fonts,
    enableRemoteSearch = true,
    layerId,
    recordId,
    isVIP,
    isIntl,
    onOpenVIPModal,
}) => {
    const editor = useEditor();
    const fontFamily = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        return textInfo ? textInfo.fontFamily : null;
    }) as string | null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const usingFonts = getUsingFonts(editor, editor.services.frame.getActiveFrameId());

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openFontFamilySelector({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
            });
        }
    };

    const handleChange = (data: FontItem, fontName: string) => {
        if (!fontFamily) return;

        editor.getInstance<VectorLayer>('VectorLayer').setFontFamily(layerId, fontName, String(data['material_id']));

        Tracker.setFontFamily({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            font: data,
            isSearch: false,
            isVectorLayer: true,
        });
    };

    return (
        <FontFamilySelector
            value={fontFamily || FALLBACK_FONT_FAMILY}
            fonts={groupBy(fonts, 'category_name')}
            usingFonts={usingFonts}
            enableRemoteSearch={!isIntl && enableRemoteSearch}
            isVip={isVIP}
            onClickVipBtn={onOpenVIPModal}
            onChange={handleChange}
            onDropdownVisibleChange={handleOpenChange}
        />
    );
};
